<template>
  <div class="file-uploader bg-slate-50/70 border border-dashed border-sky-100 rounded-xl p-3 md:p-4">
    <div class="upload-row">
      <div class="upload-button-container">
        <button class="upload-button" @click="triggerFileInput" :disabled="isProcessing || fileContexts.length >= maxFiles">
          <svg viewBox="0 0 24 24" width="16" height="16">
            <path fill="currentColor" d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
          </svg>
          上传文件
        </button>
        <input 
          type="file" 
          ref="fileInput" 
          @change="handleFileSelect" 
          multiple 
          style="display: none"
          accept=".doc,.docx,.xls,.xlsx,.pdf"
        />
      </div>
    </div>

    <div class="file-hint">
      支持格式：doc、docx、xls、xlsx、pdf，单个文件最大10M
    </div>
    
    <div v-if="fileContexts.length > 0 || isProcessing" class="files-wrapper">
      <div class="files-section-header">
        <div class="files-count">
          已上传 {{ fileContexts.filter(f => f.status === 'done' || f.status === 'error').length }}/{{ maxFiles }} 文件
        </div>
        <button class="expand-toggle-button" @click="toggleExpand">
          {{ isExpanded ? '收起' : '展开' }}
          <svg class="toggle-icon" :class="{ 'is-collapsed': !isExpanded }" viewBox="0 0 24 24" width="16" height="16">
            <path fill="currentColor" d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z"/>
          </svg>
        </button>
      </div>

      <div v-show="isExpanded" class="files-content">
        <div v-if="fileContexts.length > 0" class="files-list">
          <div v-for="(file, index) in fileContexts" :key="index" class="file-item">
            <div class="file-info">
              <span class="file-name">{{ file.filename }}</span>
              <span class="file-status" :class="file.status">{{ getStatusText(file.status) }}</span>
              <span v-if="file.error" class="file-error-tooltip">
                {{ file.error }}
                <span class="tooltip-text">{{ file.error }}</span>
              </span>
            </div>
            <button class="delete-button" @click="removeFile(index)">
              <svg viewBox="0 0 24 24" width="16" height="16">
                <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            </button>
          </div>
        </div>
        
        <div v-if="isProcessing" class="processing-indicator">
          <div class="spinner"></div>
          <span>正在处理文件...</span>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, reactive, onMounted, onUnmounted, computed } from 'vue';
import { saveAs } from 'file-saver';
// 静态引入 pdf.js 避免动态导入跨域/阻塞问题
import * as pdfjsLibStatic from 'pdfjs-dist/legacy/build/pdf';

const props = defineProps({
  maxFiles: {
    type: Number,
    default: 10
  },
  maxFileSize: {
    type: Number,
    default: 10 * 1024 * 1024 // 10MB
  }
});

const emit = defineEmits(['update:fileContexts']);

const fileInput = ref(null);
const fileContexts = ref([]);
const isProcessing = ref(false);
const isExpanded = ref(true);

// 动态加载所需库
let mammoth = null;
let XLSX = null; 
let PDFJS = pdfjsLibStatic; // 使用静态引入的实例
const BASE_URL = process.env.BASE_URL || '/';
const PDF_WORKER_SRC = `${BASE_URL}pdf.worker.min.js`;

const ensurePdfWorker = (pdfjsLib) => {
  if (pdfjsLib?.GlobalWorkerOptions) {
    pdfjsLib.GlobalWorkerOptions.workerSrc = PDF_WORKER_SRC;
  } else {
    console.warn('PDF.js GlobalWorkerOptions 不可用，无法设置 workerSrc');
  }
};

// 在组件挂载时设置 PDF worker（使用静态引入的 pdf.js）
onMounted(() => {
  try {
    ensurePdfWorker(PDFJS);
  } catch (err) {
    console.error('PDF.js worker setup failed:', err);
  }
});

const loadLibrary = async (libraryName) => {
  switch (libraryName) {
    case 'mammoth':
      if (!mammoth) {
        mammoth = await import('mammoth');
      }
      return mammoth;
    case 'xlsx':
      if (!XLSX) {
        XLSX = await import('xlsx');
      }
      return XLSX;
    case 'pdf':
      // 静态引入的 PDFJS 已可用
      return PDFJS;
    default:
      throw new Error(`Unknown library: ${libraryName}`);
  }
};

const triggerFileInput = () => {
  fileInput.value.click();
};

const handleFileSelect = async (event) => {
  const files = event.target.files;
  if (!files || files.length === 0) return;
  
  // 检查是否超过最大文件数
  const remainingSlots = props.maxFiles - fileContexts.value.length;
  const filesToProcess = Array.from(files).slice(0, remainingSlots);
  
  if (filesToProcess.length === 0) return;
  
  isProcessing.value = true;
  
  for (const file of filesToProcess) {
    // 检查文件大小
    if (file.size > props.maxFileSize) {
      const fileContext = {
        filename: file.name,
        type: file.type,
        size: file.size,
        content: '',
        status: 'error',
        error: `文件太大 (${(file.size / 1024 / 1024).toFixed(2)}MB)，不能超过${Math.round(props.maxFileSize / 1024 / 1024)}MB`
      };
      fileContexts.value.push(fileContext);
      emit('update:fileContexts', fileContexts.value);
      console.log('文件过大:', file.name);
      continue;
    }
    
    // 验证文件类型
    const fileType = getFileType(file);
    if (fileType === 'unknown') {
      const fileContext = {
        filename: file.name,
        type: file.type,
        size: file.size,
        content: '',
        status: 'error',
        error: '不支持的文件类型，仅支持Word、Excel和PDF文件'
      };
      fileContexts.value.push(fileContext);
      emit('update:fileContexts', fileContexts.value);
      console.log('不支持的文件类型:', file.name);
      continue;
    }
    
    // 添加文件到列表，状态为处理中
    const fileContext = {
      filename: file.name,
      type: file.type,
      size: file.size,
      content: '',
      status: 'processing'
    };
    
    fileContexts.value.push(fileContext);
    emit('update:fileContexts', fileContexts.value);
    console.log('文件列表更新:', fileContexts.value);
    
    try {
      await processFile(file, fileContext);
      fileContext.status = 'done';
    } catch (error) {
      console.error('处理文件时出错:', error);
      fileContext.status = 'error';
      fileContext.error = error.message || '处理文件时出错';
    }
    
    emit('update:fileContexts', fileContexts.value);
    console.log('文件处理完成:', fileContexts.value);
  }
  
  isProcessing.value = false;
  
  // 清空文件输入，允许再次选择相同文件
  event.target.value = '';
};

const processFile = async (file, fileContext) => {
  const fileType = getFileType(file);
  
  switch (fileType) {
    case 'docx':
      await processDocx(file, fileContext);
      break;
    case 'doc-legacy':
      await processDocLegacy(file, fileContext);
      break;
    case 'xlsx':
    case 'xls':
      await processExcel(file, fileContext);
      break;
    case 'pdf':
      await processPdf(file, fileContext);
      break;
    default:
      throw new Error('不支持的文件类型');
  }
};

const getFileType = (file) => {
  const name = file.name.toLowerCase();
  
  if (name.endsWith('.docx')) return 'docx';
  if (name.endsWith('.doc')) return 'doc-legacy';
  if (name.endsWith('.xlsx') || name.endsWith('.xls')) return 'xlsx';
  if (name.endsWith('.pdf')) return 'pdf';
  
  return 'unknown';
};

const processDocx = async (file, fileContext) => {
  try {
    const { default: mammoth } = await loadLibrary('mammoth');
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });
    fileContext.content = result.value.trim();
    
    if (!fileContext.content) {
      throw new Error('无法从Word文档中提取文本内容');
    }
  } catch (error) {
    console.error('处理Word文档失败:', error);
    throw new Error('处理Word文档失败: ' + (error.message || '未知错误'));
  }
};

// 简易 .doc 处理：尝试用 mammoth 直接读取；若失败给出明确提示
const processDocLegacy = async (file, fileContext) => {
  try {
    const { default: mammoth } = await loadLibrary('mammoth');
    const arrayBuffer = await file.arrayBuffer();
    // 先尝试用 mammoth（部分 .doc 也能解析）
    try {
      const result = await mammoth.extractRawText({ arrayBuffer });
      fileContext.content = (result.value || '').trim();
    } catch (mammothError) {
      console.warn('mammoth 解析 .doc 失败，尝试兜底提取', mammothError);
    }

    // 如果没有内容，做一次兜底的简单文本提取（过滤不可打印字符）
    if (!fileContext.content) {
      const text = extractPlainTextFromBinary(arrayBuffer);
      fileContext.content = text.trim();
    }
    
    if (!fileContext.content) {
      throw new Error('无法从 .doc 中提取文本内容，请另存为 .docx 或 PDF 后重试');
    }
  } catch (error) {
    console.error('处理 .doc 文件失败:', error);
    throw new Error('解析 .doc 失败，请另存为 .docx 或 PDF 再上传');
  }
};

// 粗粒度的二进制文本提取：提取可打印字符并压缩空白
const extractPlainTextFromBinary = (arrayBuffer) => {
  try {
    // 先尝试 UTF-16LE
    try {
      const utf16 = new TextDecoder('utf-16le', { fatal: false }).decode(arrayBuffer);
      const cleaned = sanitizeText(utf16);
      if (cleaned.length > 50) return cleaned;
    } catch (e) { /* ignore */ }

    // 再尝试 GB18030（中文旧版 doc 常见）
    try {
      const gb = new TextDecoder('gb18030', { fatal: false }).decode(arrayBuffer);
      const cleaned = sanitizeText(gb);
      if (cleaned.length > 50) return cleaned;
    } catch (e) { /* ignore */ }

    // 兜底 Latin1
    const latin = new TextDecoder('windows-1252', { fatal: false }).decode(arrayBuffer);
    return sanitizeText(latin);
  } catch (e) {
    console.error('extractPlainTextFromBinary error', e);
    return '';
  }
};

const sanitizeText = (text) => {
  return text
    .replace(/[\\u0000-\\u0008\\u000B\\u000C\\u000E-\\u001F]/g, ' ')
    .replace(/\\s+/g, ' ')
    .trim();
};

const processExcel = async (file, fileContext) => {
  try {
    const { utils, read } = await import('xlsx');
    const arrayBuffer = await file.arrayBuffer();
    const workbook = read(arrayBuffer, { type: 'array' });
    
    let content = '';
    
    // 处理所有工作表
    workbook.SheetNames.forEach(sheetName => {
      const worksheet = workbook.Sheets[sheetName];
      const csvData = utils.sheet_to_csv(worksheet);
      if (csvData.trim()) {
        content += `工作表: ${sheetName}\n${csvData}\n\n`;
      }
    });
    
    fileContext.content = content.trim();
    
    if (!fileContext.content) {
      throw new Error('无法从Excel文件中提取内容');
    }
  } catch (error) {
    console.error('处理Excel文件失败:', error);
    throw new Error('处理Excel文件失败: ' + (error.message || '未知错误'));
  }
};

const processPdf = async (file, fileContext) => {
  try {
    const pdfjsLib = await loadLibrary('pdf'); // 获取已配置worker的pdfjsLib实例
    // 确保 workerSrc 指向本地静态文件
    ensurePdfWorker(pdfjsLib);
    
    const arrayBuffer = await file.arrayBuffer();
    const loadingTask = pdfjsLib.getDocument({ data: new Uint8Array(arrayBuffer) });
    const pdf = await loadingTask.promise;
    
    let content = '';
    const maxPages = Math.min(pdf.numPages, 20);
    
    for (let i = 1; i <= maxPages; i++) {
      try {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map(item => item.str).join(' ');
        content += `页面 ${i}:\n${pageText}\n\n`;
      } catch (pageError) {
        console.error(`提取PDF第${i}页时出错:`, pageError);
        content += `页面 ${i}: [提取失败]\n\n`;
      }
    }
    
    if (pdf.numPages > maxPages) {
      content += `[注：文档共 ${pdf.numPages} 页，已提取前 ${maxPages} 页内容]`;
    }
    
    fileContext.content = content.trim();
    
    if (!fileContext.content && pdf.numPages > 0) { // 确保有页面但无内容时也报错
      throw new Error('无法从PDF文件中提取文本内容，文件可能为空或格式不支持。');
    }
  } catch (error) {
    console.error('处理PDF文件失败 (详细信息):', error);
    let errorMessage = '处理PDF文件失败';
    if (error.message) {
      errorMessage += ': ' + error.message;
    } else if (error.name) {
      errorMessage += ' (' + error.name + ')';
    }
    if (error.message && error.message.toLowerCase().includes('worker')) {
        errorMessage += '。请确认 public/pdf.worker.min.js 可访问，或手动刷新后重试。';
    }
    throw new Error(errorMessage);
  }
};

const removeFile = (index) => {
  fileContexts.value.splice(index, 1);
  emit('update:fileContexts', fileContexts.value);
};

const getStatusText = (status) => {
  switch (status) {
    case 'processing': return '处理中';
    case 'done': return '已完成';
    case 'error': return '处理失败';
    default: return '';
  }
};

const toggleExpand = () => {
  isExpanded.value = !isExpanded.value;
};

// 导出文件上下文供父组件使用
defineExpose({ fileContexts });
</script>

<style lang="scss" scoped>
@import '../styles/variables.scss';

.file-uploader {
  margin: $spacing-sm 0 $spacing-sm;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
  width: 100%;

  &:hover {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.04);
  }
}

.upload-row {
  display: flex;
  align-items: center;
  gap: $spacing-md;
  justify-content: space-between;
  flex-wrap: wrap;
  width: 100%;
}

.upload-button-container {
  margin-bottom: $spacing-xs;
}

.file-hint {
  font-size: 0.75rem;
  color: $text-color;
  opacity: 0.65;
  margin: $spacing-xs 0 $spacing-sm;
  padding-left: $spacing-xs;
}

.files-wrapper {
  display: flex;
  flex-direction: column;
  gap: $spacing-xs;
  width: 100%;
}

.files-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: $spacing-sm;
  padding: $spacing-xs $spacing-sm;
  background-color: rgba($primary-color, 0.04);
  border-radius: $border-radius-sm;
  border: 1px dashed rgba($primary-color, 0.15);
}

.files-count {
  font-size: 0.85rem;
  color: $text-color;
  opacity: 0.8;
}

.expand-toggle-button {
  background: none;
  border: none;
  color: $primary-color;
  cursor: pointer;
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  padding: $spacing-xs;
  border-radius: $border-radius-sm;
  &:hover {
    background-color: rgba($primary-color, 0.1);
  }
  .toggle-icon {
    margin-left: 4px;
    transition: transform 0.2s ease-in-out;
    &.is-collapsed {
      transform: rotate(180deg);
    }
  }
}

.upload-button {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: $spacing-xs $spacing-md;
  border-radius: $border-radius-lg;
  background-color: rgba($primary-color, 0.1);
  color: $primary-color;
  border: 1px solid rgba($primary-color, 0.3);
  cursor: pointer;
  font-size: 0.9rem;
  height: 40px;
  font-weight: 600;
  box-shadow: 0 6px 16px rgba($primary-color, 0.08);
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background-color: rgba($primary-color, 0.15);
    transform: translateY(-1px);
    box-shadow: 0 10px 24px rgba($primary-color, 0.14);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    margin-right: 2px;
  }
}

.files-content {
  display: flex;
  flex-direction: column;
  gap: $spacing-xs;
}

.files-list {
  background-color: rgba($background-color, 0.6);
  border-radius: $border-radius-sm;
  padding: $spacing-sm;
  max-height: 260px;
  overflow-y: auto;
  border: 1px solid rgba($background-color, 0.35);
  margin-top: $spacing-xs;
  width: 100%;
  overflow-x: hidden;
}

.files-header {
  // This class is no longer used for the main header, replaced by .files-section-header
  // font-size: 0.85rem;
  // color: $text-color;
  // opacity: 0.7;
  // margin-bottom: $spacing-xs;
}

.file-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: $spacing-xs;
  border-radius: $border-radius-sm;
  margin-bottom: $spacing-xs;
  background-color: white;
  border: 1px solid rgba($border-color, 0.5);
  
  &:last-child {
    margin-bottom: 0;
  }
}

.file-info {
  display: flex;
  align-items: center;
  gap: $spacing-sm;
  flex: 1;
  min-width: 0;
}

.file-name {
  font-size: 0.9rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
  word-break: break-all;
}

.file-status {
  font-size: 0.8rem;
  padding: 2px 6px;
  border-radius: 10px;
  white-space: nowrap;
  
  &.processing {
    background-color: #f0f7ff;
    color: #2b5fb4;
  }
  
  &.done {
    background-color: #eef8ee;
    color: #2e7b32;
  }
  
  &.error {
    background-color: #fff0f0;
    color: #c62828;
  }
}

.file-error-tooltip {
  position: relative;
  font-size: 0.8rem;
  color: #c62828;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 120px;
  cursor: help;
  margin-left: $spacing-xs;

  .tooltip-text {
    visibility: hidden;
    width: 250px;
    background-color: #555;
    color: #fff;
    text-align: center;
    border-radius: $border-radius-sm;
    padding: $spacing-sm;
    position: absolute;
    z-index: 1;
    bottom: 125%;
    left: 50%;
    margin-left: -125px;
    opacity: 0;
    transition: opacity 0.3s;
    white-space: normal;
    font-size: 0.75rem;

    &::after {
      content: "";
      position: absolute;
      top: 100%;
      left: 50%;
      margin-left: -5px;
      border-width: 5px;
      border-style: solid;
      border-color: #555 transparent transparent transparent;
    }
  }

  &:hover .tooltip-text {
    visibility: visible;
    opacity: 1;
  }
}

.delete-button {
  padding: 2px;
  border: none;
  background: transparent;
  color: rgba($text-color, 0.5);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  
  &:hover {
    background-color: rgba($border-color, 0.3);
    color: rgba($text-color, 0.8);
  }
}

.processing-indicator {
  display: flex;
  align-items: center;
  gap: $spacing-sm;
  font-size: 0.9rem;
  color: rgba($text-color, 0.7);
  padding: $spacing-xs;
  border: 1px solid rgba($background-color, 0.3);
  border-radius: $border-radius-sm;
  padding: $spacing-sm;
  margin-top: $spacing-xs;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba($primary-color, 0.3);
  border-top: 2px solid $primary-color;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@media (max-width: 640px) {
  .upload-row {
    align-items: stretch;
    flex-direction: column;
    gap: $spacing-xs;
  }

  .files-section-header {
    width: 100%;
    justify-content: space-between;
  }

  .files-list {
    max-height: 240px;
  }

  .upload-button-container,
  .files-section-header {
    align-self: stretch;
  }
}
</style> 
